// src/utils/imageUpload.ts
//
// Client uploads exactly ONE file per photo. The backend handles
// generating any reduced-resolution derivative needed for vision
// APIs server-side via sharp. This minimizes user bandwidth and
// connectivity risk — a single PUT, then if it fails we retry it.

import { ref, uploadBytes, UploadResult } from 'firebase/storage';
import { storage, auth } from '../services/firebase';
import { processImage } from './imageProcessing';

export interface UploadProgress {
  status: 'preparing' | 'uploading' | 'complete' | 'error' | 'retrying';
  progress: number;
  error?: string;
  attempt?: number;
  maxAttempts?: number;
}

export interface UploadOptions {
  maxRetries?: number;
  retryDelay?: number;
  onProgress?: (progress: UploadProgress) => void;
}

const DEFAULT_OPTIONS: Required<UploadOptions> = {
  maxRetries: 3,
  retryDelay: 1000,
  onProgress: () => {},
};

const generateImageFilename = (_conversationId: string): string => {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 8);
  return `${timestamp}_${random}.jpg`;
};

const createImagePath = (
  userId: string,
  conversationId: string,
  filename: string
): string => {
  return `images/${userId}/${conversationId}/${filename}`;
};

const sleep = (ms: number): Promise<void> => {
  return new Promise((resolve) => setTimeout(resolve, ms));
};

/**
 * Upload a single image file to Firebase Storage with retry/backoff.
 * Returns the storage path. Backend functions consume this path,
 * fetch the file server-side, and resize in-memory before passing
 * to vision APIs.
 */
export const uploadImageToStorage = async (
  imageFile: File,
  conversationId: string,
  options: UploadOptions = {}
): Promise<string> => {
  const { maxRetries, retryDelay, onProgress } = {
    ...DEFAULT_OPTIONS,
    ...options,
  };

  const user = auth.currentUser;
  if (!user) throw new Error('User not authenticated');

  let lastError: Error | null = null;
  let hasCompleted = false;

  for (let attempt = 1; attempt <= maxRetries + 1; attempt++) {
    try {
      if (attempt > 1) {
        onProgress({
          status: 'retrying',
          progress: 0,
          attempt,
          maxAttempts: maxRetries + 1,
        });
        const delay = retryDelay * Math.pow(2, attempt - 2);
        await sleep(delay);
      }

      onProgress({
        status: 'preparing',
        progress: 10,
        attempt,
        maxAttempts: maxRetries + 1,
      });

      const processed = await processImage(imageFile);
      console.log(
        `📐 Image prepared: ${(processed.size / 1024 / 1024).toFixed(2)}MB`
      );

      const filename = generateImageFilename(conversationId);
      const imagePath = createImagePath(user.uid, conversationId, filename);
      const storageRef = ref(storage, imagePath);

      onProgress({
        status: 'uploading',
        progress: 50,
        attempt,
        maxAttempts: maxRetries + 1,
      });

      const _uploadResult: UploadResult = await uploadBytes(
        storageRef,
        processed,
        {
          contentType: 'image/jpeg',
          customMetadata: {
            conversationId,
            userId: user.uid,
            originalName: imageFile.name,
            uploadedAt: new Date().toISOString(),
          },
        }
      );

      console.log(`✅ Upload successful: ${imagePath}`);

      if (!hasCompleted) {
        hasCompleted = true;
        onProgress({
          status: 'complete',
          progress: 100,
          attempt,
          maxAttempts: maxRetries + 1,
        });
      }

      return imagePath;
    } catch (error: any) {
      console.error(`❌ Upload attempt ${attempt} failed:`, error);
      lastError = error;

      if (attempt === maxRetries + 1) {
        onProgress({
          status: 'error',
          progress: 0,
          error: error.message || 'Upload failed',
          attempt,
          maxAttempts: maxRetries + 1,
        });
        break;
      }

      if (isRetriableError(error)) {
        continue;
      } else {
        onProgress({
          status: 'error',
          progress: 0,
          error: error.message || 'Upload failed',
          attempt,
          maxAttempts: maxRetries + 1,
        });
        throw error;
      }
    }
  }

  throw lastError || new Error('Upload failed after all retries');
};

const isRetriableError = (error: any): boolean => {
  if (
    error.code === 'storage/network-error' ||
    error.code === 'storage/timeout' ||
    error.message?.includes('network') ||
    error.message?.includes('timeout')
  ) {
    return true;
  }
  // 5xx server errors
  if (error.code?.startsWith('storage/server-')) return true;
  // Quota / rate limits — retry with backoff
  if (error.code === 'storage/quota-exceeded') return true;
  return false;
};

export const formatUploadStatus = (progress: UploadProgress): string => {
  switch (progress.status) {
    case 'preparing':
      return 'Preparing photo…';
    case 'uploading':
      return progress.attempt && progress.attempt > 1
        ? `Uploading (attempt ${progress.attempt})…`
        : 'Uploading photo…';
    case 'retrying':
      return `Retrying (${progress.attempt}/${progress.maxAttempts})…`;
    case 'complete':
      return 'Upload complete';
    case 'error':
      return progress.error || 'Upload failed';
    default:
      return '';
  }
};
